/**
 * Landing Reference Profile — pure inference function.
 *
 * Turns the user's monthly rent (₹) into a plausible financial profile
 * that the existing Core engines can consume. This module never contains
 * business logic beyond the constants defined in `rentProfileConfig.js`
 * — its only job is to fill the gap between "the landing page has one
 * input" and "the Core affordability engine needs six".
 *
 * Contract:
 *   inferProfileFromRent(rent)  →  null            when rent is falsy / ≤ 0
 *   inferProfileFromRent(rent)  →  { salary, obligations, downPayment,
 *                                    tenure, rate, bankRule, targetEMI,
 *                                    segment, sba }   otherwise
 *
 * The returned shape is the exact input contract of
 * `computeAffordability` in affordabilityCore.js — no adapter needed.
 */
import { LANDING_PROFILE_CONFIG as CFG } from "./rentProfileConfig";

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

export function inferProfileFromRent(rent) {
    if (!rent || rent <= 0) return null;

    // Two rent-derived inputs, both clamped to sensible Indian buyer
    // ranges. Everything else is a pass-through of the app's Core defaults.
    const salary = clamp(
        Math.round(rent * CFG.rentToSalaryMultiplier),
        CFG.clamps.salary.min,
        CFG.clamps.salary.max,
    );
    const downPayment = clamp(
        Math.round(rent * CFG.rentToDownPaymentMultiplier),
        CFG.clamps.downPayment.min,
        CFG.clamps.downPayment.max,
    );

    return {
        salary,
        obligations: CFG.defaultObligations,
        downPayment,
        tenure:      CFG.defaultTenureYears,
        rate:        CFG.defaultRatePct,
        bankRule:    CFG.defaultBankRule,
        targetEMI:   CFG.defaultTargetEMI,
        segment:     CFG.defaultSegment,
        sba:         CFG.defaultSbaSqft,
    };
}
